import React, {useEffect, useState} from "react";
import Select from "react-select";
import axios from "axios";
import apiUrl from "../../../shared/config";
import makeAnimated from "react-select/animated/dist/react-select.esm";
import chroma from "chroma-js";

/**
 * The select component used to select keywords in the search bar and to add keywords to a pattern
 * @param keywordsFilter The current keywords selected
 * @param keywordsUpdate A function that updates the value of keywords filter
 * @param optionsArr An optional field to pass the keyword options in to that the component doesn't make an api call.
 * @param updatePage an update function run on change. In current implementations I think it reloads a component.
 * @param closePatternDetails An optional function that closes the pattern details screen.
 * @param {String} [placeholderText = 'Add keywords to this pattern']
 * @return {Element}
 * @constructor
 */
export default function ImprovedKeywordInput({keywordsFilter, keywordsUpdate, optionsArr, updatePage, closePatternDetails, placeholderText = 'Add keywords to this pattern'}) {
    const [isLoading, setIsLoading] = useState(true)
    const [unmappedOptions, setUnmappedOptions] = useState([])

    // responsible for getting the basic options data. This will be called on the first render and when optionsArr is updated
    useEffect(() => {
        if (optionsArr && Array.isArray(optionsArr) && optionsArr.length > 0) {
            setUnmappedOptions(optionsArr)
            setIsLoading(false)
        } else {
            axios.get(apiUrl +'Keywords/getKeywords')
                .then((response) => {
                    setUnmappedOptions(response.data)
                    setIsLoading(false)
                })
        }
    }, [optionsArr]);

    // select component animations
    const animatedComponents = makeAnimated();
    const colourStyles = {
        control: styles => ({...styles, backgroundColor: 'white'}),
        option: (styles, {data, isDisabled, isFocused, isSelected}) => {
            const color = chroma(data.color);
            return {
                ...styles,
                backgroundColor: isDisabled
                    ? null
                    : isSelected
                        ? data.color
                        : isFocused
                            ? color.alpha(0.1).css()
                            : null,
                color: isDisabled
                    ? '#ccc'
                    : isSelected
                        ? chroma.contrast(color, 'white') > 2
                            ? 'white'
                            : 'black'
                        : data.color,
                cursor: isDisabled ? 'not-allowed' : 'default',

                ':active': {
                    ...styles[':active'],
                    backgroundColor: !isDisabled && (isSelected ? data.color : color.alpha(0.3).css()),
                },
            };
        },
        multiValue: (styles, {data}) => {
            const color = chroma(data.color);
            return {
                ...styles,
                backgroundColor: color.alpha(0.1).css(),
            };
        },
        multiValueLabel: (styles, {data}) => ({
            ...styles,
            color: data.color,
        }),
        multiValueRemove: (styles, {data}) => ({
            ...styles,
            color: data.color,
            ':hover': {
                backgroundColor: data.color,
                color: 'white',
            },
        }),
    };

    const groupStyles = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    };
    const groupBadgeStyles = {
        backgroundColor: '#EBECF0',
        borderRadius: '2em',
        color: '#172B4D',
        display: 'inline-block',
        fontSize: 12,
        fontWeight: 'normal',
        lineHeight: '1',
        minWidth: 1,
        padding: '0.16666666666667em 0.5em',
        textAlign: 'center',
    };

    const formatGroupLabel = data => (
        <div style={groupStyles}>
            <span>{data.label}</span>
            <span style={groupBadgeStyles}>{data.options.length}</span>
        </div>
    );

    const keywordOptions = unmappedOptions.filter(k => k.keyword_type === 'keyword')
        .map(k => ({value: k.keyword_id, label: k.keyword + ' - ' + k.keyword_description, color: '#8f71ff'}))

    const categoryOptions = unmappedOptions.filter(k => k.keyword_type === 'category')
        .map(k => ({value: k.keyword_id, label: k.keyword + ' - ' + k.keyword_description, color: '#990099'}))

    const propertyOptions = unmappedOptions.filter(k => k.keyword_type === 'property')
        .map(k => ({value: k.keyword_id, label: k.keyword + ' - ' + k.keyword_description, color: '#009999'}))
    // the groups that are currently selected (formatted for the select)

    const options = [
        {
            label: 'Keywords',
            options: keywordOptions,
        },
        {
            label: 'Categories',
            options: categoryOptions,
        },
        {
            label: 'Properties',
            options: propertyOptions,
        },
    ];

    // slightly more complicated to find here because of the keyword groups
    const keywords = keywordsFilter.map(id => {
        let item = {value: null, label: '', color: '#000000'}
        item = keywordOptions.find(o => +o.value === +id) || item
        item = categoryOptions.find(o => +o.value === +id) || item
        item = propertyOptions.find(o => +o.value === +id) || item
        return item;
    })


    return <Select
        isMulti
        isSearchable
        placeholder={isLoading ? "Loading keywords..." : placeholderText}
        value={keywords}
        options={options}
        onChange={(val) => {
            keywordsUpdate(val);
            updatePage(0);
            closePatternDetails && closePatternDetails(null);
        }}
        formatGroupLabel={formatGroupLabel}
        styles={colourStyles}
        components={animatedComponents}
    />

}